import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Alpha1Set,
  ModalcolorSet,
  PromodeSet,
  Ratio1Set,
  Color1Set,
  Cesium1Set,
} from "../App";
import { FunkMovebyKey } from "../coreCesium/funcMovebyKey";
import Csv2kml from "../makekml/csv2kml";
import { ButtonAlpha } from "./buttonAlpha";
import { ButtonRatio } from "./buttonRatio";
import { HanreiHigh } from "./hanreiHigh";
import { HanreiNormal } from "./hanreiNormal";
import Csv2kml_v2 from "../makekml_v2/csv2kml_v2";
import Csv2kml_v4 from "../makekml_v4/csv2kml_v4";

export const MierukaFooter = ({ metadata }) => {
  const [footerview, setFooterview] = useState(true);

  const { ratio1set } = useContext(Ratio1Set);
  const { alpha1set } = useContext(Alpha1Set);

  const { setModalcolorSet } = useContext(ModalcolorSet);
  const { promodeset, setPromodeSet } = useContext(PromodeSet);
  const { setColor1Set } = useContext(Color1Set);

  const { cesium1set } = useContext(Cesium1Set);

  const { area } = useParams();

  // --- handlers ---
  const toggleFooterView = useCallback(() => {
    setFooterview((v) => !v);
  }, []);

  const toggleProMode = useCallback(() => {
    setPromodeSet((v) => !v);
  }, [setPromodeSet]);

  const openDetailModal = useCallback(() => {
    setModalcolorSet(true);
  }, [setModalcolorSet]);

  // --- derived classnames ---
  const animClass = footerview ? "animate-fade-in-up" : "animate-fade-in-down";
  const footerBarAnimClass = footerview ? "animate-fade-in-up" : "animate-fade-out-down";

  // metadata から色配列を組み立て（hanrei*_color は nullable）
  const hanreiColors = useMemo(
    () => [
      metadata?.hanrei1_color ?? "",
      metadata?.hanrei2_color ?? "",
      metadata?.hanrei3_color ?? "",
      metadata?.hanrei4_color ?? "",
      metadata?.hanrei5_color ?? "",
    ],
    [
      metadata?.hanrei1_color,
      metadata?.hanrei2_color,
      metadata?.hanrei3_color,
      metadata?.hanrei4_color,
      metadata?.hanrei5_color,
    ]
  );

  // ✅ 折衷（Pro切替でも、metadata更新でも反映）＋ 依存は整理
  useEffect(() => {
    setColor1Set(hanreiColors);
  }, [hanreiColors, setColor1Set]);
  // }, [promodeset, hanreiColors, setColor1Set]);

  return (
    <>
      {/* フッタ表示トグル */}
      <div
        className={`absolute bottom-0 z-30 pointer-events-none md:rounded-xl bg-blue-500 ${animClass}`}
      >
        <div className="pointer-events-auto">
          <input
            name="footer"
            id="フッタ表示"
            type="checkbox"
            onChange={toggleFooterView}
            checked={!footerview} // ← 元と同一（見た目完全互換）
            className="peer hidden"
          />

          {footerview ? (
            <label
              htmlFor="フッタ表示"
              className="block cursor-pointer select-none md:rounded-sm text-center text-xl md:text-3xl lg:text-4xl bg-gray-200 text-black"
            >
              ▼
            </label>
          ) : (
            <label
              htmlFor="フッタ表示"
              className="block cursor-pointer select-none md:rounded-sm text-center text-xs md:text-sm lg:text-base peer-checked:bg-blue-500 peer-checked:text-white"
            >
              ▲表示する
            </label>
          )}
        </div>
      </div>

      {/* Proモードトグル（元挙動維持：footerview=false の時だけ表示） */}
      <div>
        <div
          className={`absolute bottom-0 left-32 z-30 pointer-events-auto md:rounded-sm bg-blue-500 ${animClass}`}
        >
          <div className="flex pointer-events-auto">
            {footerview ? null : (
              <>
                <input
                  id="prochk"
                  type="checkbox"
                  onChange={toggleProMode}
                  checked={!!promodeset}
                />
                <label
                  htmlFor="prochk"
                  className="block cursor-pointer md:rounded-sm text-center text-xs md:text-sm lg:text-base bg-blue-500 text-white"
                >
                  Proモード(開発用であり不具合がありますので使用しないでください)
                </label>
              </>
            )}
          </div>
        </div>
      </div>

      {/* メインフッタ */}
      <div
        className={`absolute z-20 bottom-0 min-w-full text-black-900 py-1 text-center bg-gradient-to-r from-white/90 via-white/70 to-white/30 pointer-events-none ${footerBarAnimClass}`}
      >
        <div className="flex md:justify-center w-full pointer-events-none">
          <div className="w-7/12 md:w-5/12">
            {promodeset ? <HanreiHigh metadata={metadata} /> : <HanreiNormal metadata={metadata} />}
          </div>

          <div className="w-1/12 flex justify-center my-auto pointer-events-auto">
            <ButtonRatio />
          </div>

          {/* {promodeset && (
            <div className="w-1/12 flex justify-center my-auto pointer-events-auto">
              <ButtonAlpha />
            </div>
          )} */}

          {promodeset && (
            <div onClick={openDetailModal} className="flex justify-center pointer-events-auto">
              <div className="flex justify-center md:rounded-xl bg-gray-200 md:p-2 my-auto mx-4">
                <div className="block cursor-pointer select-none md:rounded-xl px-0 md:px-3 py-0 md:py-7 text-center text-sm md:text-base lg:text-xl bold bg-blue-500 text-white">
                  詳細設定
                </div>
              </div>
            </div>
          )}

          {promodeset && <FunkMovebyKey rfcs={cesium1set} />}

          {/* <div className="w-3/12 md:w-3/12 flex justify-center pointer-events-none">
            <div className="flex justify-center md:rounded-xl bg-gray-200 md:p-2 my-auto">
              <div className="block cursor-pointer pointer-events-auto select-none md:rounded-xl px-0 md:px-3 py-0 md:py-7 text-center text-sm md:text-base lg:text-xl bold bg-black text-white">
                <Csv2kml meta={metadata} area={area} ratio={ratio1set} alpha={alpha1set}>
                  <p>
                    Google Earth
                    <span className="hidden md:block">で可視化する</span>
                  </p>
                </Csv2kml>
              </div>
            </div>
          </div> */}
          <div className="w-3/12 md:w-3/12 flex justify-center pointer-events-none">
            <div className="flex justify-center md:rounded-xl bg-gray-200 md:p-2 my-auto">
              <div className="block cursor-pointer pointer-events-auto select-none md:rounded-xl px-0 md:px-3 py-0 md:py-7 text-center text-sm md:text-base lg:text-xl bold bg-black text-white">
                <Csv2kml_v2 meta={metadata} area={area} ratio={ratio1set} alpha={alpha1set}>
                  <p>
                    Google Earth
                    <span className="hidden md:block">で可視化する</span>
                  </p>
                </Csv2kml_v2>
              </div>
            </div>
          </div>
          {promodeset &&
            <div className="w-3/12 md:w-3/12 flex justify-center pointer-events-none">
              <div className="flex justify-center md:rounded-xl bg-gray-200 md:p-2 my-auto">
                <div className="block cursor-pointer pointer-events-auto select-none md:rounded-xl px-0 md:px-3 py-0 md:py-7 text-center text-sm md:text-base lg:text-xl bold bg-black text-white">
                  <Csv2kml_v4 meta={metadata} area={area} ratio={ratio1set} alpha={alpha1set}>
                    <p>
                      Google Earth
                      <span className="hidden md:block">サーバ生成版</span>
                    </p>
                  </Csv2kml_v4>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </>
  );
};